import { apiClient, ApiResponse } from './client'
import type { Language } from '../store/settingsStore'

// Enum/Metadata Services
export interface EnumItem {
  code: string
  /** Single-locale label when the API returns one language per request. */
  label?: string | Record<string, string>
  /** All locale labels when the API returns them in one payload. */
  labels?: Partial<Record<Language, string>> | Record<string, string>
}

export interface EnumsResponse {
  [enumName: string]: EnumItem[]
}

export const metadataService = {
  /** Labels are localized server-side from the authenticated user's locale (JWT). */
  getEnums: async (): Promise<EnumsResponse> => {
    const response = await apiClient.get<ApiResponse<EnumsResponse>>('/api/v1/metadata/enums')
    return response.data.data || response.data
  },
}

// Subscription Services
export type SubscriptionStatus =
  | 'PENDING'
  | 'TRIAL'
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'CANCELLED'
  | 'EXPIRED'
  | 'SUSPENDED'

export interface SubscriptionDto {
  id: string
  accountId: string
  planId: string
  status: SubscriptionStatus
  startDate: string
  endDate?: string
  trialEndDate?: string
  nextBillingDate?: string
  lastBillingDate?: string
  currentPeriodStart?: string
  currentPeriodEnd?: string
  monthlyPrice?: number
  currency?: string
  autoRenew?: boolean
  gracePeriodEnd?: string
  cancelledAt?: string
  cancellationReason?: string
  cancellationType?: string
  discountAmount?: number
  discountPercentage?: number
  discountEndDate?: string
  promotionalCode?: string
  discountScope?: DiscountScope
  freeUntilDate?: string
  chargeMonthlyFeeOnly?: boolean
  bonusIncludedTons?: number
  bonusIncludedJobs?: number
  overageDiscountPercentage?: number
  bounceOriginalPlanId?: string
  bounceEndDate?: string
  bounceReason?: string
  createdAt?: string
  updatedAt?: string
}

export type DiscountScope = 'MONTHLY_FEE' | 'USAGE' | 'TOTAL'

export interface ApplyDiscountRequest {
  discountAmount?: number
  discountPercentage?: number
  discountEndDate?: string
  discountScope?: DiscountScope
  reason: string
  promotionalCode?: string
}

export interface GrantFreePeriodRequest {
  freeUntilDate?: string
  days?: number
  reason: string
}

export interface SetBillingOptionsRequest {
  chargeMonthlyFeeOnly?: boolean
  bonusIncludedTons?: number
  bonusIncludedJobs?: number
  overageDiscountPercentage?: number
  reason: string
}

export interface BouncePlanRequest {
  newPlanId: string
  bounceEndDate?: string
  days?: number
  reason: string
}

export interface ChangePlanRequest {
  newPlanId: string
  prorate?: boolean
  startTrial?: boolean
  reason?: string
}

export interface SubscriptionInvoiceDto {
  id: string
  invoiceNumber?: string
  status?: string
  billingPeriodStart?: string
  billingPeriodEnd?: string
  issueDate?: string
  dueDate?: string
  paidDate?: string
  subtotal?: number
  taxAmount?: number
  discountAmount?: number
  totalAmount?: number
  currency?: string
  paymentMethod?: string
  downloadUrl?: string
  attemptCount?: number
  nextPaymentAttempt?: string
  failureReason?: string
  invoiceItems?: string
  createdAt?: string
}

export const subscriptionService = {
  getAll: async (page = 0, size = 20, status?: string, planType?: string) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/subscriptions',
      { params: { page, size, status, planType } }
    )
    return response.data.data
  },

  getById: async (subscriptionId: string) => {
    const response = await apiClient.get<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}`
    )
    return response.data.data
  },

  extendTrial: async (subscriptionId: string, days: number) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/extend-trial`,
      null,
      { params: { days } }
    )
    return response.data.data
  },

  applyDiscount: async (subscriptionId: string, request: ApplyDiscountRequest) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/apply-discount`,
      request
    )
    return response.data.data
  },

  removeDiscount: async (subscriptionId: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/remove-discount`
    )
    return response.data.data
  },

  grantFreePeriod: async (subscriptionId: string, request: GrantFreePeriodRequest) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/grant-free-period`,
      request
    )
    return response.data.data
  },

  endFreePeriod: async (subscriptionId: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/end-free-period`
    )
    return response.data.data
  },

  setBillingOptions: async (subscriptionId: string, request: SetBillingOptionsRequest) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/billing-options`,
      request
    )
    return response.data.data
  },

  bouncePlan: async (subscriptionId: string, request: BouncePlanRequest) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/bounce-plan`,
      request
    )
    return response.data.data
  },

  revertBounce: async (subscriptionId: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/revert-bounce`
    )
    return response.data.data
  },

  forceCancel: async (subscriptionId: string, reason: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/force-cancel`,
      null,
      { params: { reason } }
    )
    return response.data.data
  },

  reactivate: async (subscriptionId: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/${subscriptionId}/reactivate`
    )
    return response.data.data
  },

  changePlan: async (accountId: string, request: ChangePlanRequest) => {
    const response = await apiClient.post<ApiResponse<SubscriptionDto>>(
      `/api/v1/admin/subscriptions/accounts/${accountId}/change-plan`,
      request
    )
    return response.data.data
  },

  getOverdueInvoices: async (page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/subscriptions/invoices/overdue',
      { params: { page, size } }
    )
    return response.data.data
  },

  markInvoicePaid: async (invoiceId: string, paymentMethod: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionInvoiceDto>>(
      `/api/v1/admin/subscriptions/invoices/${invoiceId}/mark-paid`,
      null,
      { params: { paymentMethod } }
    )
    return response.data.data
  },

  sendBulkReminders: async (type: string, subscriptionIds?: string[]) => {
    const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
      '/api/v1/admin/subscriptions/bulk/send-reminders',
      null,
      { params: { type, subscriptionIds } }
    )
    return response.data.data
  },
}

// Subscription Plans Services
export type AccountType = 'SHIPPER' | 'FREIGHTER'
export type BillingCycle = 'MONTHLY' | 'QUARTERLY' | 'YEARLY'

export interface SubscriptionPlanDto {
  id: string
  planCode?: string
  name: string
  description?: string
  accountType?: AccountType
  billingCycle?: BillingCycle
  currency?: string
  trialPeriodDays?: number
  country?: string
  monthlyFee?: number
  isActive?: boolean
  isFeatured?: boolean
  sortOrder?: number
  features?: string
  limitations?: string
  // Freighter-specific
  maxJobsPerMonth?: number
  perJobCommission?: number
  // Shipper-specific
  includedTonsPerMonth?: number
  includedAreaM2PerMonth?: number
  includedVolumeM3PerMonth?: number
  perTonFee?: number
  perAreaM2Fee?: number
  perVolumeM3Fee?: number
  hasPriorityMatching?: boolean
}

export interface CreatePlanRequest {
  planCode: string
  name: string
  description?: string
  accountType: AccountType
  billingCycle: BillingCycle
  price: number
  currency?: string
  trialPeriodDays?: number
  country: string
  monthlyFee?: number
  maxJobsPerMonth?: number
  perJobCommission?: number
  includedTonsPerMonth?: number
  includedAreaM2PerMonth?: number
  includedVolumeM3PerMonth?: number
  perTonFee?: number
  perAreaM2Fee?: number
  perVolumeM3Fee?: number
  hasPriorityMatching?: boolean
  isFeatured?: boolean
  sortOrder?: number
  features?: string
  limitations?: string
}

export interface UpdatePlanRequest {
  name?: string
  description?: string
  monthlyFee?: number
  trialPeriodDays?: number
  maxJobsPerMonth?: number
  perJobCommission?: number
  includedTonsPerMonth?: number
  includedAreaM2PerMonth?: number
  includedVolumeM3PerMonth?: number
  perTonFee?: number
  perAreaM2Fee?: number
  perVolumeM3Fee?: number
  hasPriorityMatching?: boolean
  isFeatured?: boolean
  sortOrder?: number
  features?: string
  limitations?: string
}

export const subscriptionPlanService = {
  getAll: async (includeInactive = false, page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/subscription-plans',
      { params: { includeInactive, page, size } }
    )
    return response.data.data
  },

  create: async (plan: CreatePlanRequest) => {
    const response = await apiClient.post<ApiResponse<SubscriptionPlanDto>>(
      '/api/v1/admin/subscription-plans',
      plan
    )
    return response.data.data
  },

  update: async (planId: string, plan: UpdatePlanRequest) => {
    const response = await apiClient.put<ApiResponse<SubscriptionPlanDto>>(
      `/api/v1/admin/subscription-plans/${planId}`,
      plan
    )
    return response.data.data
  },

  activate: async (planId: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionPlanDto>>(
      `/api/v1/admin/subscription-plans/${planId}/activate`
    )
    return response.data.data
  },

  deactivate: async (planId: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionPlanDto>>(
      `/api/v1/admin/subscription-plans/${planId}/deactivate`
    )
    return response.data.data
  },

  getAnalytics: async (planId: string) => {
    const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
      `/api/v1/admin/subscription-plans/${planId}/analytics`
    )
    return response.data.data
  },

  duplicate: async (planId: string, newName: string, newCode: string) => {
    const response = await apiClient.post<ApiResponse<SubscriptionPlanDto>>(
      `/api/v1/admin/subscription-plans/${planId}/duplicate`,
      null,
      { params: { newName, newCode } }
    )
    return response.data.data
  },
}

// Entitlement (plan add-on) Services
export type EntitlementValueType = 'BOOLEAN' | 'LIMIT'

export interface EntitlementDto {
  id: string
  entitlementKey: string
  name: string
  descriptionKey?: string
  accountType: AccountType
  valueType: EntitlementValueType
  defaultEnabled?: boolean
  isActive?: boolean
  sortOrder?: number
}

export interface CreateEntitlementRequest {
  entitlementKey: string
  name: string
  descriptionKey?: string
  accountType: AccountType
  valueType: EntitlementValueType
  defaultEnabled?: boolean
  isActive?: boolean
  sortOrder?: number
}

export interface UpdateEntitlementRequest {
  name?: string
  descriptionKey?: string
  valueType?: EntitlementValueType
  defaultEnabled?: boolean
  isActive?: boolean
  sortOrder?: number
}

/** A plan → entitlement mapping row, flattened with catalog metadata. */
export interface PlanEntitlementDto {
  id?: string
  planId: string
  entitlementId: string
  entitlementKey: string
  name: string
  valueType: EntitlementValueType
  enabled: boolean
  limitValue?: number | null
}

export interface SetPlanEntitlementRequest {
  enabled: boolean
  limitValue?: number | null
}

export const entitlementService = {
  // --- Catalog ---
  listCatalog: async (accountType?: AccountType) => {
    const response = await apiClient.get<ApiResponse<EntitlementDto[]>>(
      '/api/v1/admin/subscriptions/entitlements',
      { params: accountType ? { accountType } : undefined }
    )
    return response.data.data
  },
  create: async (body: CreateEntitlementRequest) => {
    const response = await apiClient.post<ApiResponse<EntitlementDto>>(
      '/api/v1/admin/subscriptions/entitlements',
      body
    )
    return response.data.data
  },
  update: async (entitlementId: string, body: UpdateEntitlementRequest) => {
    const response = await apiClient.put<ApiResponse<EntitlementDto>>(
      `/api/v1/admin/subscriptions/entitlements/${entitlementId}`,
      body
    )
    return response.data.data
  },
  // --- Per-plan mappings ---
  listForPlan: async (planId: string) => {
    const response = await apiClient.get<ApiResponse<PlanEntitlementDto[]>>(
      `/api/v1/admin/subscriptions/entitlements/plans/${planId}`
    )
    return response.data.data
  },
  setForPlan: async (planId: string, entitlementId: string, body: SetPlanEntitlementRequest) => {
    const response = await apiClient.put<ApiResponse<PlanEntitlementDto>>(
      `/api/v1/admin/subscriptions/entitlements/plans/${planId}/${entitlementId}`,
      body
    )
    return response.data.data
  },
}

// Configuration Services
export interface ConfigSectionResponse {
  section: string
  source: 'appconfig' | 'in-memory'
  editable: boolean
  persisted: boolean
  values: Record<string, any>
}

/** Body shape can be either the section wrapper or a bare values map (legacy/local). */
const unwrapConfig = (raw: any): { values: Record<string, any>; meta: Partial<ConfigSectionResponse> } => {
  if (raw && typeof raw === 'object' && 'values' in raw && raw.values && typeof raw.values === 'object') {
    return {
      values: raw.values as Record<string, any>,
      meta: {
        section: raw.section,
        source: raw.source,
        editable: raw.editable,
        persisted: raw.persisted,
      },
    }
  }
  return { values: (raw ?? {}) as Record<string, any>, meta: {} }
}

const wrapConfigCall = async (request: Promise<any>): Promise<ConfigSectionResponse> => {
  const response = await request
  const { values, meta } = unwrapConfig(response.data.data)
  return {
    section: meta.section ?? 'unknown',
    source: (meta.source as ConfigSectionResponse['source']) ?? 'in-memory',
    editable: meta.editable ?? true,
    persisted: meta.persisted ?? false,
    values,
  }
}

export const configService = {
  getConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config')),
  updateConfig: async (config: Record<string, any>) =>
    wrapConfigCall(
      apiClient.put<ApiResponse<any>>('/api/v1/admin/config', {
        configuration: config,
      }),
    ),
  getPricingConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config/pricing')),
  updatePricingConfig: async (config: Record<string, any>) =>
    wrapConfigCall(
      apiClient.put<ApiResponse<any>>('/api/v1/admin/config/pricing', {
        configuration: config,
      }),
    ),
  getRoutingConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config/routing')),
  updateRoutingConfig: async (config: Record<string, any>) =>
    wrapConfigCall(
      apiClient.put<ApiResponse<any>>('/api/v1/admin/config/routing', {
        configuration: config,
      }),
    ),
  getMatchingConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config/matching')),
  updateMatchingConfig: async (config: Record<string, any>) =>
    wrapConfigCall(
      apiClient.put<ApiResponse<any>>('/api/v1/admin/config/matching', {
        configuration: config,
      }),
    ),
  getPenaltyConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config/penalties')),
  updatePenaltyConfig: async (config: Record<string, any>) =>
    wrapConfigCall(
      apiClient.put<ApiResponse<any>>('/api/v1/admin/config/penalties', {
        configuration: config,
      }),
    ),
  getScoringConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config/scoring')),
  updateScoringConfig: async (config: Record<string, any>) =>
    wrapConfigCall(apiClient.put<ApiResponse<any>>('/api/v1/admin/config/scoring', config)),
  getReceiverConfirmationConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config/receiver-confirmation')),
  updateReceiverConfirmationConfig: async (config: Record<string, any>) =>
    wrapConfigCall(
      apiClient.put<ApiResponse<any>>('/api/v1/admin/config/receiver-confirmation', {
        configuration: config,
      }),
    ),
  getMarketHoursConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config/market-hours')),
  updateMarketHoursConfig: async (config: Record<string, any>) =>
    wrapConfigCall(
      apiClient.put<ApiResponse<any>>('/api/v1/admin/config/market-hours', {
        configuration: config,
      }),
    ),
  getDriverReminderConfig: async () =>
    wrapConfigCall(apiClient.get<ApiResponse<any>>('/api/v1/admin/config/driver-reminder')),
  updateDriverReminderConfig: async (config: Record<string, any>) =>
    wrapConfigCall(
      apiClient.put<ApiResponse<any>>('/api/v1/admin/config/driver-reminder', {
        configuration: config,
      }),
    ),
  refreshCache: async () => {
    const response = await apiClient.post<ApiResponse<void>>('/api/v1/admin/config/refresh')
    return response.data
  },
  getCacheStats: async () => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/admin/config/cache-stats')
    return response.data.data
  },
}

// Score Services
export interface AdjustScoreRequest {
  adjustment: number
  reason: string
}

export const scoreService = {
  adjustScore: async (entityType: string, entityId: string, request: AdjustScoreRequest) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/v1/admin/scores/${entityType}/${entityId}/adjust`,
      request
    )
    return response.data
  },
  resetScore: async (entityType: string, entityId: string, reason: string) => {
    const response = await apiClient.put<ApiResponse<void>>(
      `/api/v1/admin/scores/${entityType}/${entityId}/reset`,
      { reason }
    )
    return response.data
  },
  getScore: async (entityType: string, entityId: string) => {
    const response = await apiClient.get<ApiResponse<any>>(
      `/api/v1/admin/scores/${entityType}/${entityId}`
    )
    return response.data.data
  },
}

// Analytics Services
export const analyticsService = {
  getSubscriptionOverview: async () => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/subscriptions/analytics/admin/overview'
    )
    return response.data.data
  },
  getRevenueAnalytics: async (startDate: string, endDate: string) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/subscriptions/analytics/admin/revenue',
      { params: { startDate, endDate } }
    )
    return response.data.data
  },
  getChurnAnalytics: async (days = 30) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/subscriptions/analytics/admin/churn',
      { params: { days } }
    )
    return response.data.data
  },
}

// Account Services
export interface AccountDto {
  id: string
  type: string
  name: string
  businessName: string
  businessAddress: string
  vatNum?: string
  contactEmail?: string
  contactPhone?: string
  rating?: number
  billingAddress?: string
  businessLegalId?: string
  paymentMethod?: string
  defaultPaymentTerms?: string
  defaultCurrency?: string
  subscriptionStartDate?: string
  subscriptionEndDate?: string
  /** Account lifecycle status (single source of truth): ACTIVE | INACTIVE | PENDING_PROFILE. */
  status: string
  organizationCode?: string
  createdAt: string
}

export const accountService = {
  getAll: async (page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/accounts/all', {
      params: { page, size },
    })
    return response.data.data
  },
  getById: async (accountId: string) => {
    const response = await apiClient.get<ApiResponse<AccountDto>>(`/api/v1/accounts/${accountId}`)
    return response.data.data
  },
  update: async (accountId: string, account: any) => {
    const response = await apiClient.put<ApiResponse<AccountDto>>(
      `/api/v1/accounts/${accountId}`,
      account
    )
    return response.data.data
  },
  deactivate: async (accountId: string) => {
    const response = await apiClient.put<ApiResponse<void>>(
      `/api/v1/accounts/${accountId}/deactivate`
    )
    return response.data
  },
  activate: async (accountId: string) => {
    const response = await apiClient.put<ApiResponse<void>>(
      `/api/v1/accounts/${accountId}/activate`
    )
    return response.data
  },
  getUsersByAccount: async (accountId: string, page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>(
      `/api/v1/accounts/users/${accountId}`,
      { params: { page, size } }
    )
    return response.data.data
  },
  getStatistics: async () => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/accounts/statistics/admin')
    return response.data.data
  },
}

// User Services
export type UserStatus = 'ACTIVE' | 'PENDING_APPROVAL' | 'DEACTIVATED'
export type UserRole =
  | 'SHIPPER_DISTRIBUTION_MANAGER'
  | 'SHIPPER_OBSERVER'
  | 'FREIGHTER_FOREMAN'
  | 'FREIGHTER_DRIVER'
  | 'SYSTEM_ADMIN'
  | 'SHIPMENT_RECEIVER'
  | 'TRAFFIC_OFFICER'
  | 'ROAD_SERVICE'

export interface UserDto {
  id: string
  accountId: string
  name: string
  nickName?: string
  email?: string
  phone?: string
  roles: UserRole[]
  status: UserStatus
  locale?: string
  timezone?: string
  lastLoginAt?: string
  rating?: number
  ratingCount?: number
}

export interface InviteUserRequest {
  name: string
  email?: string
  phone: string
  roles: UserRole[]
}

export const userService = {
  getById: async (userId: string) => {
    const response = await apiClient.get<ApiResponse<UserDto>>(`/api/v1/users/${userId}`)
    return response.data.data
  },
  update: async (userId: string, user: Partial<UserDto>) => {
    const response = await apiClient.put<ApiResponse<UserDto>>(`/api/v1/users/${userId}`, user)
    return response.data.data
  },
  invite: async (request: InviteUserRequest) => {
    const response = await apiClient.post<ApiResponse<UserDto>>(
      '/api/v1/users/account/invite',
      request
    )
    return response.data.data
  },
  delete: async (userId: string) => {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/v1/users/${userId}`)
    return response.data
  },
  activate: async (userId: string) => {
    const response = await apiClient.put<ApiResponse<UserDto>>(`/api/v1/users/${userId}/activate`)
    return response.data.data
  },
  deactivate: async (userId: string) => {
    const response = await apiClient.put<ApiResponse<UserDto>>(`/api/v1/users/${userId}/deactivate`)
    return response.data.data
  },
  resendInvitation: async (userId: string) => {
    const response = await apiClient.post<ApiResponse<void>>(`/api/v1/users/${userId}/resend-invitation`)
    return response.data
  },
  /**
   * System-admin override: set a new password for a user who cannot self-serve forgot-password.
   * The admin then shares the password with the user. Requires an already-set-up account (Cognito
   * identity); the backend rejects users who have not yet joined/accepted.
   */
  setPassword: async (userId: string, newPassword: string) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/v1/auth/admin/users/${userId}/set-password`,
      { newPassword }
    )
    return response.data
  },
}

// Driver Services
export interface DriverDto {
  id: string
  /** Backend Driver entities don't carry accountId in the DTO directly — it's derived via user. */
  accountId?: string
  licenseNo: string
  licenseExpiry: string
  licenseImg?: string
  kycVerificationStatus?: KycVerificationStatus
  kycVerifiedAt?: string
  kycVerifiedBy?: UserDto
  user?: UserDto
  createdAt?: string
  updatedAt?: string
}

export const driverService = {
  getAllByAccount: async (accountId: string) => {
    const response = await apiClient.get<ApiResponse<DriverDto[]>>(
      `/api/v1/drivers/account/${accountId}`
    )
    return response.data.data
  },
  getAll: async (page = 0, size = 20, accountId?: string) => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/drivers', {
      params: { page, size, accountId },
    })
    return response.data.data
  },
  getById: async (driverId: string) => {
    const response = await apiClient.get<ApiResponse<DriverDto>>(`/api/v1/drivers/${driverId}`)
    return response.data.data
  },
  /** Base64-encoded file content (backend serializes byte[] as base64). */
  getLicenseImage: async (driverId: string) => {
    const response = await apiClient.get<ApiResponse<string | null>>(
      `/api/v1/drivers/${driverId}/license-image`
    )
    return response.data.data
  },
  update: async (driverId: string, driver: Partial<DriverDto>) => {
    const response = await apiClient.put<ApiResponse<DriverDto>>(`/api/v1/drivers/${driverId}`, driver)
    return response.data.data
  },
  delete: async (driverId: string, deleteUser = false) => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/v1/drivers/${driverId}`,
      { params: { deleteUser } }
    )
    return response.data
  },
  create: async (request: any) => {
    const response = await apiClient.post<ApiResponse<DriverDto>>('/api/v1/drivers', request)
    return response.data.data
  },
  getUnavailability: async (driverId: string) => {
    const response = await apiClient.get<ApiResponse<DriverUnavailabilityDto[]>>(
      `/api/v1/drivers/${driverId}/unavailability`,
    )
    return response.data.data
  },
  addUnavailability: async (driverId: string, request: VehicleUnavailabilityWriteRequest) => {
    const response = await apiClient.post<ApiResponse<DriverUnavailabilityDto>>(
      `/api/v1/drivers/${driverId}/unavailability`,
      request,
    )
    return response.data.data
  },
  updateUnavailability: async (
    unavailabilityId: string,
    request: VehicleUnavailabilityWriteRequest,
  ) => {
    const response = await apiClient.put<ApiResponse<DriverUnavailabilityDto>>(
      `/api/v1/drivers/unavailability/${unavailabilityId}`,
      request,
    )
    return response.data.data
  },
  removeUnavailability: async (unavailabilityId: string) => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/v1/drivers/unavailability/${unavailabilityId}`,
    )
    return response.data
  },
}

export interface DriverUnavailabilityDto {
  id: string
  driverId: string
  unavailabilityType: UnavailabilityType
  startDateTime?: string
  endDateTime?: string
  dayOfWeek?: DayOfWeek
  startTimeOfDay?: string
  endTimeOfDay?: string
  dailyStartTime?: string
  dailyEndTime?: string
  startDate?: string
  endDate?: string
  reason?: string
  createdAt?: string
  updatedAt?: string
}

// Vehicle Services
export type VehicleType =
  | 'VAN'
  | 'PICKUP_TRUCK'
  | 'SMALL_TRUCK_3_5_TON'
  | 'MEDIUM_TRUCK_7_5_TON'
  | 'LARGE_TRUCK_12_TON'
  | 'SEMI_TRAILER'

export type LoadingSide = 'BACK' | 'SIDE' | 'TOP' | 'BACK_SIDE'

export interface VehicleDto {
  id: string
  accountId: string
  defaultDriverId?: string
  plateNo: string
  licenseExpiry?: string
  lastTestDate?: string
  maker?: string
  model?: string
  year?: number
  type?: VehicleType
  capacityKg?: number
  lengthM?: number
  widthM?: number
  heightM?: number
  boxTruck?: boolean
  curtainsider?: boolean
  flatbed?: boolean
  hasHazmat?: boolean
  hasRefrigeration?: boolean
  hasRamp?: boolean
  loadingSide?: LoadingSide
  homeBaseLatitude?: number
  homeBaseLongitude?: number
  homeBaseAddress?: string
  rating?: number
  ratingCount?: number
  kycVerificationStatus?: KycVerificationStatus
  kycVerifiedAt?: string
  kycVerifiedBy?: UserDto
  fuelConsumptionPer100Km?: number
  vehicleImgs?: string[]
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
}

export interface VehiclePictureDto {
  /** S3 key of the picture. */
  documentId: string
  /** Base64-encoded picture content. */
  content: string | null
}

export const vehicleService = {
  getAllByAccount: async (accountId: string) => {
    const response = await apiClient.get<ApiResponse<VehicleDto[]>>(
      `/api/v1/vehicles/account/${accountId}`
    )
    return response.data.data
  },
  getAll: async (page = 0, size = 20, accountId?: string) => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/vehicles', {
      params: { page, size, accountId },
    })
    return response.data.data
  },
  getById: async (vehicleId: string) => {
    const response = await apiClient.get<ApiResponse<VehicleDto>>(`/api/v1/vehicles/${vehicleId}`)
    return response.data.data
  },
  /** Document downloads return base64-encoded content, or null when nothing was uploaded. */
  getLicenseImage: async (vehicleId: string) => {
    const response = await apiClient.get<ApiResponse<string | null>>(
      `/api/v1/vehicles/${vehicleId}/license-image`
    )
    return response.data.data
  },
  getPlateImage: async (vehicleId: string) => {
    const response = await apiClient.get<ApiResponse<string | null>>(
      `/api/v1/vehicles/${vehicleId}/plate-image`
    )
    return response.data.data
  },
  getInspectionCertificate: async (vehicleId: string) => {
    const response = await apiClient.get<ApiResponse<string | null>>(
      `/api/v1/vehicles/${vehicleId}/inspection-certificate`
    )
    return response.data.data
  },
  getPictures: async (vehicleId: string) => {
    const response = await apiClient.get<ApiResponse<VehiclePictureDto[]>>(
      `/api/v1/vehicles/${vehicleId}/vehicle-pictures`
    )
    return response.data.data
  },
  getByPlate: async (plateNumber: string) => {
    const response = await apiClient.get<ApiResponse<VehicleDto>>(
      `/api/v1/vehicles/plate/${encodeURIComponent(plateNumber)}`
    )
    return response.data.data
  },
  update: async (vehicleId: string, vehicle: Partial<VehicleDto>) => {
    const response = await apiClient.put<ApiResponse<VehicleDto>>(
      `/api/v1/vehicles/${vehicleId}`,
      vehicle
    )
    return response.data.data
  },
  delete: async (vehicleId: string) => {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/v1/vehicles/${vehicleId}`)
    return response.data
  },
  activate: async (vehicleId: string) => {
    const response = await apiClient.put<ApiResponse<VehicleDto>>(
      `/api/v1/admin/vehicles/${vehicleId}/activate`,
    )
    return response.data.data
  },
  deactivate: async (vehicleId: string) => {
    const response = await apiClient.put<ApiResponse<VehicleDto>>(
      `/api/v1/admin/vehicles/${vehicleId}/deactivate`,
    )
    return response.data.data
  },
  create: async (request: any) => {
    const response = await apiClient.post<ApiResponse<VehicleDto>>('/api/v1/vehicles', request)
    return response.data.data
  },
  getUnavailability: async (vehicleId: string) => {
    const response = await apiClient.get<ApiResponse<VehicleUnavailabilityDto[]>>(
      `/api/v1/vehicles/${vehicleId}/unavailability`,
    )
    return response.data.data
  },
  addUnavailability: async (vehicleId: string, request: VehicleUnavailabilityWriteRequest) => {
    const response = await apiClient.post<ApiResponse<VehicleUnavailabilityDto>>(
      `/api/v1/vehicles/${vehicleId}/unavailability`,
      request,
    )
    return response.data.data
  },
  updateUnavailability: async (
    vehicleId: string,
    unavailabilityId: string,
    request: VehicleUnavailabilityWriteRequest,
  ) => {
    const response = await apiClient.put<ApiResponse<VehicleUnavailabilityDto>>(
      `/api/v1/vehicles/${vehicleId}/unavailability/${unavailabilityId}`,
      request,
    )
    return response.data.data
  },
  removeUnavailability: async (vehicleId: string, unavailabilityId: string) => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/v1/vehicles/${vehicleId}/unavailability/${unavailabilityId}`,
    )
    return response.data
  },
}

export type UnavailabilityType =
  | 'ONE_TIME'
  | 'ASSIGNED'
  | 'RECURRING_WEEKLY'
  | 'RECURRING_DAILY'
  | 'DATE_RANGE'

export type DayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY'

export interface VehicleUnavailabilityDto {
  id: string
  vehicleId: string
  unavailabilityType: UnavailabilityType
  startDateTime?: string
  endDateTime?: string
  dayOfWeek?: DayOfWeek
  startTimeOfDay?: string
  endTimeOfDay?: string
  dailyStartTime?: string
  dailyEndTime?: string
  startDate?: string
  endDate?: string
  reason?: string
  createdAt?: string
  updatedAt?: string
}

export interface VehicleUnavailabilityWriteRequest {
  unavailabilityType: UnavailabilityType
  startDateTime?: string | null
  endDateTime?: string | null
  dayOfWeek?: DayOfWeek | null
  startTimeOfDay?: string | null
  endTimeOfDay?: string | null
  dailyStartTime?: string | null
  dailyEndTime?: string | null
  startDate?: string | null
  endDate?: string | null
  reason?: string | null
}

// Vehicle rating admin
export const vehicleRatingService = {
  resetRating: async (vehicleId: string) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/v1/admin/vehicles/${vehicleId}/rating/reset`
    )
    return response.data
  },
}

// User & account rating admin (used by Drivers page — driver rating lives on the underlying user)
export const userRatingService = {
  resetUserRating: async (userId: string) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/v1/admin/users/${userId}/rating/reset`
    )
    return response.data
  },
  resetAccountRating: async (accountId: string) => {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/v1/admin/accounts/${accountId}/rating/reset`
    )
    return response.data
  },
}

// KYC Services
export type KycSubjectType = 'DRIVER' | 'VEHICLE'
export type KycVerificationStatus = 'PENDING' | 'VERIFIED' | 'REJECTED' | 'EXPIRED' | 'SUSPENDED'

export interface VerificationRequestDto {
  id: string
  subjectId: string
  subjectType: KycSubjectType
  status: KycVerificationStatus
  accountId: string
  createdAt: string
  s3Bucket?: string
  s3Key?: string
  assignedTo?: string
  assignedAt?: string
  dueDate?: string
  reviewedAt?: string
  reviewedBy?: string
  verificationNotes?: string
  rejectionReason?: string
  autoVerificationScore?: number
  requiresManualReview?: boolean
}

export interface DocumentAnalysisResult {
  documentType?: string
  confidenceScore?: number
  extractedText?: string
  extractedFields?: Record<string, unknown>
  isRecognized?: boolean
  errorMessage?: string
  documentNumber?: string
  issueDate?: string
  expiryDate?: string
  issuingAuthority?: string
  fullName?: string
  idNumber?: string
  address?: string
  licenseClass?: string
  restrictions?: string
  plateNumber?: string
  vehicleType?: string
  make?: string
  model?: string
  year?: number
  color?: string
  engineNumber?: string
  chassisNumber?: string
  firstName?: string
  lastName?: string
  fatherName?: string
  motherName?: string
  birthDate?: string
  gender?: string
  nationality?: string
}

export interface CreateVerificationRequest {
  subjectId: string
  accountId: string
  subjectType: KycSubjectType
  s3Bucket?: string
  s3Key?: string
}

export const kycService = {
  getPending: async (page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/kyc/verification/pending',
      { params: { page, size } }
    )
    return response.data.data
  },
  getById: async (verificationId: string) => {
    const response = await apiClient.get<ApiResponse<VerificationRequestDto>>(
      `/api/v1/kyc/verification/${verificationId}`
    )
    return response.data.data
  },
  update: async (verificationId: string, request: Partial<VerificationRequestDto>) => {
    const response = await apiClient.put<ApiResponse<VerificationRequestDto>>(
      `/api/v1/kyc/verification/${verificationId}`,
      request
    )
    return response.data.data
  },
  /** Marks a driver or vehicle KYC request as verified (PUT /api/v1/kyc/verification/{id}). */
  verify: async (
    verificationRequestId: string,
    row: Pick<VerificationRequestDto, 'subjectId' | 'accountId' | 'subjectType'>,
    verificationNotes?: string
  ) => {
    const response = await apiClient.put<ApiResponse<VerificationRequestDto>>(
      `/api/v1/kyc/verification/${verificationRequestId}`,
      {
        status: 'VERIFIED',
        subjectId: row.subjectId,
        accountId: row.accountId,
        subjectType: row.subjectType,
        verificationNotes: verificationNotes ?? '',
      }
    )
    return response.data.data
  },
  reject: async (
    verificationRequestId: string,
    row: Pick<VerificationRequestDto, 'subjectId' | 'accountId' | 'subjectType'>,
    rejectionReason: string,
    verificationNotes?: string
  ) => {
    const response = await apiClient.put<ApiResponse<VerificationRequestDto>>(
      `/api/v1/kyc/verification/${verificationRequestId}`,
      {
        status: 'REJECTED',
        subjectId: row.subjectId,
        accountId: row.accountId,
        subjectType: row.subjectType,
        rejectionReason,
        verificationNotes: verificationNotes ?? '',
      }
    )
    return response.data.data
  },
  assignOfficer: async (verificationId: string, officerId: string) => {
    const response = await apiClient.post<ApiResponse<VerificationRequestDto>>(
      `/api/v1/kyc/verification/${verificationId}/assign/${officerId}`
    )
    return response.data.data
  },
  initiate: async (request: CreateVerificationRequest) => {
    const response = await apiClient.post<VerificationRequestDto>(
      '/api/v1/kyc/verification/initiate',
      request
    )
    return response.data
  },
  getAiAssessment: async (verificationId: string) => {
    const response = await apiClient.post<ApiResponse<DocumentAnalysisResult>>(
      `/api/v1/kyc/verification/${verificationId}/ai-assessment`
    )
    return response.data.data
  },
  analyzeDocument: async (request: {
    s3Bucket: string
    s3Key: string
    documentType: string
  }) => {
    const response = await apiClient.post<ApiResponse<DocumentAnalysisResult>>(
      '/api/v1/kyc/verification/analyze',
      request
    )
    return response.data.data
  },
  getOfficerAssignments: async (officerId: string, page = 0, size = 20) => {
    const response = await apiClient.get<any>(
      `/api/v1/kyc/verification/officer/${officerId}`,
      { params: { page, size } }
    )
    return response.data
  },
}

// Dispute Services
export type DisputeStatus =
  | 'INITIATED'
  | 'UNDER_REVIEW'
  | 'MEDIATION'
  | 'RESOLVED'
  | 'CLOSED'
  | 'ESCALATED'
  | 'WITHDRAWN'
  | 'EXPIRED'

export type DisputeResolutionType =
  | 'WITHDRAWN'
  | 'MUTUAL_AGREEMENT'
  | 'MEDIATION_DECISION'
  | 'FULL_REFUND'
  | 'PARTIAL_REFUND'
  | 'PAYOUT_ADJUSTMENT'
  | 'HOLD_PAYMENT'
  | 'RELEASE_PAYMENT'

export type DisputeResponseType = 'ACCEPT' | 'ACCEPT_PARTIAL' | 'REJECT' | 'ACKNOWLEDGE'

export interface DisputeEvidenceDto {
  id: string
  disputeId: string
  uploadedBy?: string
  uploaderType?: string
  evidenceType?: string
  title?: string
  description?: string
  fileKey?: string
  createdAt?: string
}

export interface DisputeResponseDto {
  id: string
  disputeId: string
  responseType?: DisputeResponseType
  comments?: string
  senderUserId?: string
  senderAccountId?: string
  proposedAmount?: number
  counterAmount?: number
  agreedAmount?: number
  additionalEvidence?: DisputeEvidenceDto[]
  respondedAt?: string
  isFinalResponse?: boolean
  nextDueDate?: string
}

export interface DisputeDto {
  id: string
  shipmentId?: string
  jobId?: string
  disputeType: string
  title?: string
  comments?: string
  status: DisputeStatus
  resolutionType?: DisputeResolutionType
  resolutionNotes?: string
  initiatorUserId?: string
  initiatorAccountId?: string
  respondentUserId?: string
  respondentAccountId?: string
  mediatorUserId?: string
  mediatorAssignedAt?: string
  claimedAmount?: number
  dueDate?: string
  resolvedAt?: string
  resolvedByUserId?: string
  settlementAmount?: number
  currency?: string
  priority?: string
  escalatedAt?: string
  evidences?: DisputeEvidenceDto[]
  responses?: DisputeResponseDto[]
  chatRoomId?: string
  /** PENALTY_CONTEST: the contested penalty charge (Penalties & Compensations). */
  penaltyTransactionId?: string
  /** @deprecated server doesn't return a top-level createdAt on DisputeDto. */
  createdAt?: string
}

export type PenaltyDisputeOutcome = 'UPHELD' | 'REDUCED' | 'CANCELLED'

export interface ResolveDisputeRequest {
  /** Required for every type but PENALTY_CONTEST, which is resolved by penaltyOutcome. */
  resolutionType?: DisputeResolutionType
  ruling: string
  /** PENALTY_CONTEST: the ruling on the penalty, applied to the ledger before the dispute is recorded. */
  penaltyOutcome?: PenaltyDisputeOutcome
  penaltyAdjustmentMode?: PenaltyAdjustmentMode
  penaltyAdjustmentValue?: number
  awardedToAccountId?: string
  awardedAmount?: number
  currency?: string
  paymentDueDate?: string
  termsAndConditions?: string
  isFinal?: boolean
  complianceRequired?: boolean
}

export const disputeService = {
  getAll: async (page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/disputes/all', {
      params: { page, size },
    })
    return response.data.data
  },
  getById: async (disputeId: string) => {
    const response = await apiClient.get<ApiResponse<DisputeDto>>(`/api/v1/disputes/${disputeId}`)
    return response.data.data
  },
  getOverdue: async (page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/disputes/overdue', {
      params: { page, size },
    })
    return response.data.data
  },
  updateStatus: async (disputeId: string, status: DisputeStatus, reason?: string) => {
    const response = await apiClient.put<ApiResponse<DisputeDto>>(
      `/api/v1/disputes/${disputeId}/status`,
      { status, reason }
    )
    return response.data.data
  },
  assignMediator: async (disputeId: string, mediatorId: string) => {
    const response = await apiClient.post<ApiResponse<DisputeDto>>(
      `/api/v1/disputes/${disputeId}/assign-mediator`,
      { mediatorId }
    )
    return response.data.data
  },
  resolve: async (disputeId: string, request: ResolveDisputeRequest) => {
    const response = await apiClient.post<ApiResponse<DisputeDto>>(
      `/api/v1/disputes/${disputeId}/resolve`,
      request
    )
    return response.data.data
  },
  escalate: async (disputeId: string, reason?: string) => {
    const response = await apiClient.post<ApiResponse<DisputeDto>>(
      `/api/v1/disputes/${disputeId}/escalate`,
      null,
      { params: { reason } }
    )
    return response.data.data
  },
  close: async (disputeId: string) => {
    const response = await apiClient.post<ApiResponse<DisputeDto>>(
      `/api/v1/disputes/${disputeId}/close`
    )
    return response.data.data
  },
  reopen: async (disputeId: string) => {
    const response = await apiClient.post<ApiResponse<DisputeDto>>(
      `/api/v1/disputes/${disputeId}/reopen`
    )
    return response.data.data
  },
  getResponses: async (disputeId: string) => {
    const response = await apiClient.get<ApiResponse<DisputeResponseDto[]>>(
      `/api/v1/disputes/${disputeId}/responses`
    )
    return response.data.data
  },
  addResponse: async (
    disputeId: string,
    request: { responseType?: DisputeResponseType; comments?: string; proposedAmount?: number }
  ) => {
    const response = await apiClient.post<ApiResponse<DisputeResponseDto>>(
      `/api/v1/disputes/${disputeId}/responses`,
      request
    )
    return response.data.data
  },
  getEvidence: async (disputeId: string) => {
    const response = await apiClient.get<ApiResponse<DisputeEvidenceDto[]>>(
      `/api/v1/disputes/${disputeId}/evidence`
    )
    return response.data.data
  },
  getStatistics: async () => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/disputes/statistics/admin')
    return response.data.data
  },
}

// Finance Services
export interface PaymentDto {
  id: string
  shipmentId?: string
  jobId?: string
  payerAccountId?: string
  payeeAccountId?: string
  terms?: string
  grossAmount: number
  systemFee?: number
  currency?: string
  status: string
  /** When payment is due per terms. */
  dueDate?: string
  /** When payment was actually settled. */
  paidDate?: string
  heldAt?: string
  releasedAt?: string
  failureReason?: string
  invoiceNumber?: string
  refundedAmount?: number
  refundReason?: string
  wireTransferInstructions?: string
  bankWireReference?: string
  lastReminderSent?: string
  reminderCount?: number
  externalPaymentId?: string
  createdAt: string
  updatedAt?: string
  /** @deprecated use paidDate */
  paymentDate?: string
  /** @deprecated kept for backwards compatibility with older UI rendering. */
  amount?: number
  /** @deprecated use payerAccountId / payeeAccountId. */
  accountId?: string
  /** @deprecated alias for bankWireReference. */
  referenceNumber?: string
  /** @deprecated use terms. */
  paymentMethod?: string
}

export interface PayoutDto {
  id: string
  shipmentId?: string
  jobId?: string
  payerAccountId?: string
  payeeAccountId?: string
  terms?: string
  amount: number
  currency?: string
  paymentProcessor?: string
  processorResponse?: string
  status: string
  invoiceNumber?: string
  /** When payout is due per terms. */
  dueDate?: string
  /** When payout was actually settled. */
  paidDate?: string
  failedAt?: string
  failureReason?: string
  payoutMetadata?: string
  externalPayoutId?: string
  createdAt: string
  updatedAt?: string
  /** @deprecated use dueDate */
  payoutDate?: string
  /** @deprecated use paidDate */
  processedAt?: string
  /** @deprecated alias for dueDate */
  scheduledDate?: string
  /** @deprecated use payerAccountId / payeeAccountId. */
  accountId?: string
}

export interface FinanceListParams {
  page?: number
  size?: number
  status?: string
  accountId?: string
  startDate?: string
  endDate?: string
}

export const financeService = {
  getPayment: async (paymentId: string) => {
    const response = await apiClient.get<ApiResponse<PaymentDto>>(
      `/api/v1/finance/admin/payments/${paymentId}`
    )
    return response.data.data
  },
  getPendingPayments: async () => {
    const response = await apiClient.get<ApiResponse<PaymentDto[]>>(
      '/api/v1/finance/admin/payments/pending'
    )
    return response.data.data
  },
  getAllPayments: async (params: FinanceListParams = {}) => {
    const { page = 0, size = 20, status, accountId, startDate, endDate } = params
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/finance/admin/payments', {
      params: { page, size, status, accountId, startDate, endDate },
    })
    return response.data.data
  },
  refundPayment: async (
    paymentId: string,
    request: { amount: number; reason: string; adminNotes?: string }
  ) => {
    const response = await apiClient.post<ApiResponse<PaymentDto>>(
      `/api/v1/finance/admin/payments/${paymentId}/refund`,
      request
    )
    return response.data.data
  },
  getPayout: async (payoutId: string) => {
    const response = await apiClient.get<ApiResponse<PayoutDto>>(
      `/api/v1/finance/admin/payouts/${payoutId}`
    )
    return response.data.data
  },
  getScheduledPayouts: async () => {
    const response = await apiClient.get<ApiResponse<PayoutDto[]>>(
      '/api/v1/finance/admin/payouts/scheduled'
    )
    return response.data.data
  },
  getAllPayouts: async (params: FinanceListParams = {}) => {
    const { page = 0, size = 20, status, accountId, startDate, endDate } = params
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/finance/admin/payouts', {
      params: { page, size, status, accountId, startDate, endDate },
    })
    return response.data.data
  },
  cancelPayout: async (
    payoutId: string,
    request: { reason: string; adminNotes?: string }
  ) => {
    const response = await apiClient.post<ApiResponse<PayoutDto>>(
      `/api/v1/finance/admin/payouts/${payoutId}/cancel`,
      request
    )
    return response.data.data
  },
  getPendingWireTransfers: async () => {
    const response = await apiClient.get<ApiResponse<PaymentDto[]>>(
      '/api/v1/finance/admin/wire-transfers/pending'
    )
    return response.data.data
  },
  getOverdueWireTransfers: async () => {
    const response = await apiClient.get<ApiResponse<PaymentDto[]>>(
      '/api/v1/finance/admin/wire-transfers/overdue'
    )
    return response.data.data
  },
  reconcileWireTransfer: async (
    paymentId: string,
    request: { bankReference: string; notes?: string; reconciledBy?: string }
  ) => {
    const response = await apiClient.post<ApiResponse<PaymentDto>>(
      `/api/v1/finance/admin/wire-transfers/${paymentId}/reconcile`,
      request
    )
    return response.data.data
  },
  triggerProcessPayments: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/finance/admin/trigger/process-payments'
    )
    return response.data
  },
  triggerProcessPayouts: async () => {
    const response = await apiClient.post<ApiResponse<void>>(
      '/api/v1/finance/admin/trigger/process-payouts'
    )
    return response.data
  },
  getSupportedPaymentMethods: async () => {
    const response = await apiClient.get<ApiResponse<string[]>>(
      '/api/v1/finance/admin/supported-payment-methods'
    )
    return response.data.data
  },
}

// Location Services
export interface LiveTrackingDto {
  jobId?: string
  driverId: string
  vehicleId?: string
  currentLatitude: number
  currentLongitude: number
  currentSpeed?: number
  heading?: number
  lastUpdateTime: string
  batteryLevel?: number
  networkType?: string
  isOnline: boolean
}

const getLiveTrackingForAccount = async (accountId: string) => {
  const response = await apiClient.get<ApiResponse<LiveTrackingDto[]>>(
    `/api/v1/location/live/${accountId}`
  )
  return response.data.data
}

export const locationService = {
  getLiveTracking: getLiveTrackingForAccount,
  /** Platform-wide live tracking in a single call (admin endpoint). */
  getAllVehiclesLocations: async () => {
    const response = await apiClient.get<ApiResponse<LiveTrackingDto[]>>(
      '/api/v1/location/live'
    )
    return response.data.data || []
  },
}

// Shipment Services
export type ShipmentStatus =
  | 'PENDING_CONFIRMATION'
  | 'PENDING_MATCHING'
  | 'PLANNED'
  | 'PICKED_UP'
  | 'IN_TRANSIT'
  | 'DELIVERED'
  | 'LODGE'
  | 'PROBLEM'
  | 'NO_SHOW'
  | 'CANCELLED'
  | 'PENDING_SUPPLEMENT'

export interface CargoDto {
  id: string
  description?: string
  weightKg?: number
  deckAreaM2?: number
  volumeM3?: number
  deliveryAddress?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  receiverName?: string
  receiverPhone?: string
}

export interface ShipmentDto {
  id: string
  shipmentRequestId?: string
  cargos?: CargoDto[]
  jobOfferIds?: string[]
  jobId?: string
  urgency?: string
  pickupAddress: string
  pickupLatitude?: number
  pickupLongitude?: number
  pickupContactName?: string
  pickupContactPhone?: string
  pickupWindowStart?: string
  pickupWindowEnd?: string
  estimatedDistanceKm?: number
  estimatedDurationMinutes?: number
  totalWeightKg?: number
  totalDeckAreaM2?: number
  totalCargoVolumeM3?: number
  offeredPrice?: number
  currency?: string
  specialInstructions?: string
  status: ShipmentStatus
  assignedAt?: string
  pickedUpAt?: string
  chatRoomId?: string
  vehicleId?: string
  shipperAccountId?: string
  freighterAccountId?: string
  shipperAccountRating?: { businessName?: string; aggregate?: number; count?: number }
  vehicleRating?: { aggregate?: number; count?: number }
  freighterAccountRating?: { businessName?: string; aggregate?: number; count?: number }
  assignedDriver?: { id?: string; name?: string; phone?: string }
  createdAt?: string
  updatedAt?: string
}

export interface ShipmentTimelineEvent {
  type: string
  timestamp: string
  label: string
  status?: string
  details?: string
}

export interface ShipmentStatisticsDto {
  totalShipments: number
  pendingShipments: number
  inTransitShipments: number
  deliveredShipments?: number
  completedShipments: number
  cancelledShipments: number
  totalRevenue: number
  averageDeliveryTime?: number
  onTimeDeliveryRate?: number
}

export const shipmentService = {
  getAll: async (params: {
    page?: number
    size?: number
    status?: string
    shipperAccountId?: string
    freighterAccountId?: string
    from?: string
    to?: string
  } = {}) => {
    const { page = 0, size = 20, status, shipperAccountId, freighterAccountId, from, to } = params
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/admin/shipments', {
      params: { page, size, status, shipperAccountId, freighterAccountId, from, to },
    })
    return response.data.data
  },
  getById: async (shipmentId: string) => {
    const response = await apiClient.get<ApiResponse<ShipmentDto>>(`/api/v1/shipments/${shipmentId}`)
    return response.data.data
  },
  getByShipperAccount: async (accountId: string, page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>(`/api/v1/shipments/shipper/${accountId}`, {
      params: { page, size },
    })
    return response.data.data
  },
  getByFreighterAccount: async (accountId: string, page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>(`/api/v1/shipments/freighter/${accountId}`, {
      params: { page, size },
    })
    return response.data.data
  },
  getStatistics: async () => {
    const response = await apiClient.get<ApiResponse<ShipmentStatisticsDto>>(
      '/api/v1/shipments/statistics/system-admin'
    )
    return response.data.data
  },
  getByShipmentRequestId: async (shipmentRequestId: string) => {
    const response = await apiClient.get<ApiResponse<ShipmentDto>>(
      `/api/v1/shipments/shipment-request/${shipmentRequestId}`
    )
    return response.data.data
  },
  updateStatus: async (shipmentId: string, status: string, reason?: string) => {
    const response = await apiClient.put<ApiResponse<ShipmentDto>>(
      `/api/v1/shipments/${shipmentId}/status`,
      null,
      { params: { status, reason } }
    )
    return response.data.data
  },
  cancel: async (shipmentId: string, reason?: string) => {
    const response = await apiClient.post<ApiResponse<ShipmentDto>>(
      `/api/v1/shipments/${shipmentId}/cancel`,
      null,
      { params: { reason } }
    )
    return response.data.data
  },
  getShipmentRequestsByTimeWindow: async (from: string, to: string, page = 0, size = 1000) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/shipments/shipment-requests/time-window',
      { params: { from, to, page, size } }
    )
    return response.data.data
  },
  resetShipmentRequestToNew: async (requestId: string) => {
    const response = await apiClient.post<ApiResponse<ShipmentRequestDto>>(
      `/api/v1/admin/shipments/shipment-requests/${requestId}/reset-to-new`
    )
    return response.data.data
  },
  getJobsByTimeWindow: async (from: string, to: string, page = 0, size = 1000) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/shipments/jobs/time-window',
      { params: { from, to, page, size } }
    )
    return response.data.data
  },
  getJobOffersByTimeWindow: async (from: string, to: string, page = 0, size = 1000) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/shipments/job-offers/time-window',
      { params: { from, to, page, size } }
    )
    return response.data.data
  },
  /** Same payload as Shipper app GET /api/v1/shipper/preferred-vehicles, scoped to a shipper account. */
  getShipperPreferredVehicles: async (shipperAccountId: string) => {
    const response = await apiClient.get<ApiResponse<ShipperPreferredVehicleDto>>(
      `/api/v1/admin/shipments/shippers/${shipperAccountId}/preferred-vehicles`
    )
    return response.data.data
  },
  /** Same payload as Freighter app GET /api/v1/freighter/blocked-shippers, scoped to a freighter account. */
  getFreighterBlockedShippers: async (freighterAccountId: string) => {
    const response = await apiClient.get<ApiResponse<FreighterBlockedShipperDto[]>>(
      `/api/v1/admin/shipments/freighters/${freighterAccountId}/blocked-shippers`
    )
    return response.data.data
  },
  /** Same payload as Freighter app GET /api/v1/freighter/private-fleet, scoped to a freighter account. */
  getFreighterPrivateFleet: async (freighterAccountId: string) => {
    const response = await apiClient.get<ApiResponse<FreighterPrivateFleetDto>>(
      `/api/v1/admin/shipments/freighters/${freighterAccountId}/private-fleet`
    )
    return response.data.data
  },
  getTimeline: async (shipmentId: string) => {
    const response = await apiClient.get<ApiResponse<ShipmentTimelineEvent[]>>(
      `/api/v1/shipments/${shipmentId}/timeline`
    )
    return response.data.data
  },
  getRecent: async (limit = 10) => {
    const response = await apiClient.get<ApiResponse<ShipmentDto[]>>('/api/v1/admin/shipments/recent', {
      params: { limit },
    })
    return response.data.data
  },
  getPendingMatching: async (page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/admin/shipments/pending-matching', {
      params: { page, size },
    })
    return response.data.data
  },
}

// Receiver SMS Opt-out Services
export interface ReceiverSmsOptOutDto {
  id: string
  /** Masked phone (last 4 digits visible), e.g. ******6053 */
  maskedPhone: string
  optedOutAt: string
  source?: string
  createdAt?: string
}

export interface PagedResult<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export const receiverOptOutService = {
  /** List receivers who opted out of confirmation SMS (most recent first). */
  list: async (page = 0, size = 20) => {
    const response = await apiClient.get<ApiResponse<PagedResult<ReceiverSmsOptOutDto>>>(
      '/api/v1/admin/shipments/receiver-sms-opt-outs',
      { params: { page, size } },
    )
    return response.data.data
  },
  /** Remove an opt-out ("remove the removal") so the receiver gets confirmation SMS again. */
  remove: async (optOutId: string) => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/v1/admin/shipments/receiver-sms-opt-outs/${optOutId}`,
    )
    return response.data
  },
}

// Cargo returns (docs/design/return-flow.md) — Ops decides the fault of returns awaiting review.
export interface AdminCustodyFixDto {
  id: string
  action: string
  latitude?: number | null
  longitude?: number | null
  accuracyM?: number | null
  recordedAt: string
  distanceFromExpectedM?: number | null
  cargoReturnId?: string | null
}

export interface AdminCargoReturnDto {
  id: string
  cargoId: string
  shipmentId: string
  shipmentRequestId?: string | null
  jobId: string
  shipperAccountId: string
  freighterAccountId?: string | null
  cargoType?: string | null
  cargoUnits?: number | null
  dropoffBusinessName?: string | null
  dropoffAddress?: string | null
  reason: string
  notes?: string | null
  requestedAt: string
  state: string
  settledBy?: string | null
  refusalSignerName?: string | null
  returnAddress?: string | null
  handedBackAt?: string | null
  fault?: string | null
  faultFinal: boolean
  contestedAt?: string | null
  contestNotes?: string | null
  faultDecisionNotes?: string | null
  returnLegAmount?: number | null
  documents?: { id: string; documentType: string; fileName?: string | null; description?: string | null }[]
  custodyFixes?: AdminCustodyFixDto[]
}

export const cargoReturnAdminService = {
  /** Damage / missing / other returns, and contested unconfirmed returns — oldest first. */
  awaitingFault: async () => {
    const response = await apiClient.get<ApiResponse<AdminCargoReturnDto[]>>(
      '/api/v1/admin/shipments/returns/awaiting-fault',
    )
    return response.data.data
  },
  decideFault: async (returnId: string, fault: 'SHIPPER' | 'FREIGHTER', notes?: string) => {
    const response = await apiClient.post<ApiResponse<AdminCargoReturnDto>>(
      `/api/v1/admin/shipments/returns/${returnId}/fault`,
      { fault, notes },
    )
    return response.data.data
  },
}

// Penalty & Compensation Services (subscription-service, SYSTEM_ADMIN).
// Resolving customer complaints about penalties: cancel (waive), re-price, or grant a goodwill
// compensation. See docs/design/penalty-mechanism.md "Admin resolution" in the backend repo.
export type PenaltyTransactionType = 'CHARGE' | 'COMPENSATION'
export type PenaltyTransactionStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
export type PenaltyAdminAction = 'ADJUST' | 'CANCEL' | 'MANUAL_COMPENSATION'
export type PenaltyAdjustmentMode = 'AMOUNT' | 'PERCENTAGE'
export const PENALTY_REASONS = [
  'NO_SHOW',
  'CANCEL_JOB',
  'CANCEL_SHIPMENT',
  'LATE_PICKUP',
  'LATE_DELIVERY',
  'CARGO_NOT_READY',
  'FREIGHTER_FAULT_RETURN',
  'OTHER',
] as const
export type PenaltyReason = (typeof PENALTY_REASONS)[number]

export interface PenaltyAdjustmentDto {
  id: string
  penaltyTransactionId: string
  action: PenaltyAdminAction
  previousAmount?: number | null
  newAmount?: number | null
  previousPlatformFeeAmount?: number | null
  newPlatformFeeAmount?: number | null
  previousStatus?: PenaltyTransactionStatus | null
  newStatus?: PenaltyTransactionStatus | null
  reason: string
  referenceId?: string | null
  performedBy?: string | null
  stripeRefundId?: string | null
  cascadedFromId?: string | null
  accountNotified: boolean
  createdAt: string
}

export interface AdminPenaltyTransactionDto {
  id: string
  accountId: string
  /** SHIPPER | FREIGHTER — from the account's subscription, else implied by the incident. */
  accountType?: string | null
  jobId?: string | null
  shipmentId?: string | null
  driverId?: string | null
  vehicleId?: string | null
  transactionType: PenaltyTransactionType
  penaltyReason: PenaltyReason
  amount: number
  /** What the incident originally assessed; set once an admin re-prices the row. */
  originalAmount?: number | null
  platformFeeAmount?: number | null
  currency: string
  status: PenaltyTransactionStatus
  adminAction?: PenaltyAdminAction | null
  adminActionAt?: string | null
  adminCreated: boolean
  /** The reason (and reference) the admin gave on the latest action on this row. */
  lastAdminReason?: string | null
  lastAdminReferenceId?: string | null
  /** Until when the charged party may contest the incident. */
  contestableUntil?: string | null
  /** When the charged party contested it (charges only). One contest per charge. */
  contestedAt?: string | null
  /**
   * A compensation waiting on the re-placement of a Freighter's dropped work: owed only if the cargo is not
   * matched again (cancelled when it is, or re-priced to the unrescued share). Not billed until resolved.
   */
  rescueId?: string | null
  rescueResolvedAt?: string | null
  /**
   * The penalty-contest dispute on this incident, if any (kept after it ends). Linked a moment after the contest,
   * once dispute-service has opened the case.
   */
  disputeId?: string | null
  /** Set while that contest is open: held out of billing, and only the dispute can change it. */
  heldAt?: string | null
  /** When a monthly billing cycle took the row; null = billed with the next invoice. */
  billedAt?: string | null
  invoiceId?: string | null
  processedAt?: string | null
  failureReason?: string | null
  notes?: string | null
  stripePaymentIntentId?: string | null
  stripeRefundId?: string | null
  createdAt: string
  updatedAt?: string | null
  /** Detail view only: the other side of the same incident. */
  counterparts?: AdminPenaltyTransactionDto[] | null
  /** Detail view only: admin history of this row and its counterparts, newest first. */
  adjustments?: PenaltyAdjustmentDto[] | null
}

export interface PenaltySearchParams {
  page?: number
  size?: number
  accountId?: string
  jobId?: string
  shipmentId?: string
  transactionType?: PenaltyTransactionType
  penaltyReason?: PenaltyReason
  status?: PenaltyTransactionStatus
  adminTouched?: boolean
  from?: string
  to?: string
}

export interface SpringPage<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export interface CancelPenaltyRequest {
  reason: string
  referenceId?: string
  cascadeToCounterpart?: boolean
  notifyAccount?: boolean
}

export interface AdjustPenaltyRequest {
  mode: PenaltyAdjustmentMode
  value: number
  reason: string
  referenceId?: string
  cascadeToCounterpart?: boolean
  notifyAccount?: boolean
}

export interface ManualCompensationRequest {
  accountId: string
  amount: number
  currency?: string
  penaltyReason?: PenaltyReason
  jobId?: string
  shipmentId?: string
  reason: string
  referenceId?: string
  notifyAccount?: boolean
}

/** A contested penalty: who was charged, who answers the contest, and what is at stake. */
export interface PenaltyContestDetailsDto {
  penaltyTransactionId: string
  chargedAccountId: string
  respondentAccountId?: string | null
  amount: number
  compensationAmount?: number | null
  currency: string
  penaltyReason: string
  jobId?: string | null
  shipmentId?: string | null
  contestableUntil?: string | null
}

export const penaltyAdminService = {
  search: async (params: PenaltySearchParams) => {
    const response = await apiClient.get<ApiResponse<SpringPage<AdminPenaltyTransactionDto>>>(
      '/api/v1/admin/penalties',
      { params },
    )
    return response.data.data
  },
  getById: async (transactionId: string) => {
    const response = await apiClient.get<ApiResponse<AdminPenaltyTransactionDto>>(
      `/api/v1/admin/penalties/${transactionId}`,
    )
    return response.data.data
  },
  cancel: async (transactionId: string, request: CancelPenaltyRequest) => {
    const response = await apiClient.post<ApiResponse<AdminPenaltyTransactionDto>>(
      `/api/v1/admin/penalties/${transactionId}/cancel`,
      request,
    )
    return response.data.data
  },
  /** Cancel the charge and its compensation(s) together, from either side: nobody pays, nobody is paid. */
  cancelIncident: async (transactionId: string, request: CancelPenaltyRequest) => {
    const response = await apiClient.post<ApiResponse<AdminPenaltyTransactionDto>>(
      `/api/v1/admin/penalties/${transactionId}/cancel-incident`,
      request,
    )
    return response.data.data
  },
  adjust: async (transactionId: string, request: AdjustPenaltyRequest) => {
    const response = await apiClient.post<ApiResponse<AdminPenaltyTransactionDto>>(
      `/api/v1/admin/penalties/${transactionId}/adjust`,
      request,
    )
    return response.data.data
  },
  /**
   * Contest a penalty on the charged party's behalf (e.g. a phone complaint). Only within the penalty's contest
   * window, once. The penalty and its compensation are held at once; the dispute case opens a moment later.
   */
  contest: async (transactionId: string, comments: string, title?: string) => {
    const response = await apiClient.post<ApiResponse<PenaltyContestDetailsDto>>(
      `/api/v1/admin/penalties/${transactionId}/contest`,
      { comments, title },
    )
    return response.data.data
  },
  grantCompensation: async (request: ManualCompensationRequest) => {
    const response = await apiClient.post<ApiResponse<AdminPenaltyTransactionDto>>(
      '/api/v1/admin/penalties/compensations',
      request,
    )
    return response.data.data
  },
}

// Shipment Request Services
export interface FreighterBlockedShipperDto {
  shipperAccountId: string
  shipperBusinessName?: string | null
  sourceJobOfferId?: string | null
  sourceJobId?: string | null
  blockedAt?: string | null
}

export interface FreighterPrivateFleetDto {
  wholeFleet: boolean
  vehicleIds: string[]
  allowedShipperAccountIds: string[]
  shippers: {
    shipperAccountId: string
    businessName?: string | null
    markedPrivateFleet: boolean
    allowed: boolean
  }[]
}

export interface ShipperPreferredVehicleDto {
  plateNumbers: string[]
  preferredFreighterAccountIds?: string[]
  privateFleetFreighterAccountIds?: string[]
  resolvedVehicleIds?: string[]
  resolvedCount?: number
}

export interface ShipmentRequestDto {
  id: string
  shipperAccountId: string
  status: string
  pickupAddress: string
  deliveryAddress: string
  pickupLatitude?: number
  pickupLongitude?: number
  deliveryLatitude?: number
  deliveryLongitude?: number
  scheduledPickupTime?: string
  cargoType?: string
  cargoWeight?: number
  cargoVolume?: number
  cargoDescription?: string
  specialInstructions?: string
  price?: number
  currency?: string
  shipmentId?: string
  createdAt: string
  updatedAt?: string
}

export interface CargoDto {
  id: string
  shipmentRequestId: string
  cargoType: string
  weight: number
  volume?: number
  description?: string
  specialInstructions?: string
}

export const shipmentRequestService = {
  search: async (searchCriteria: any) => {
    const response = await apiClient.post<ApiResponse<any>>('/api/v1/shipment-requests/search', searchCriteria)
    return response.data.data
  },
  getPendingConfirmation: async () => {
    const response = await apiClient.get<ApiResponse<ShipmentRequestDto[]>>(
      '/api/v1/shipment-requests/pending-confirmation'
    )
    return response.data.data
  },
  getToday: async () => {
    const response = await apiClient.get<ApiResponse<ShipmentRequestDto[]>>('/api/v1/shipment-requests/today')
    return response.data.data
  },
  getDraft: async () => {
    const response = await apiClient.get<ApiResponse<ShipmentRequestDto[]>>('/api/v1/shipment-requests/draft')
    return response.data.data
  },
  getById: async (requestId: string) => {
    const response = await apiClient.get<ApiResponse<ShipmentRequestDto>>(
      `/api/v1/shipment-requests/${requestId}`
    )
    return response.data.data
  },
  update: async (requestId: string, request: any) => {
    const response = await apiClient.put<ApiResponse<ShipmentRequestDto>>(
      `/api/v1/shipment-requests/${requestId}`,
      request
    )
    return response.data.data
  },
  confirm: async (requestId: string) => {
    const response = await apiClient.post<ApiResponse<ShipmentRequestDto>>(
      `/api/v1/shipment-requests/${requestId}/confirm`
    )
    return response.data.data
  },
  cancel: async (requestId: string) => {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/v1/shipment-requests/${requestId}`)
    return response.data
  },
  rematch: async (requestId: string) => {
    const response = await apiClient.post<ApiResponse<ShipmentRequestDto>>(
      `/api/v1/shipment-requests/${requestId}/rematch`
    )
    return response.data.data
  },
}

// Job Offer Services
export interface JobStopDto {
  id: string
  sequence: number
  type: string
  latitude: number | null
  longitude: number | null
  address: string | null
  businessName: string | null
  plannedArrival?: string | null
  cargoIds?: string[]
  partnerVehicleId?: string | null
  partnerDriverId?: string | null
}

export interface JobOfferDto {
  id: string
  accountId?: string
  shipments?: ShipmentDto[]
  jobStops?: JobStopDto[]
  shipmentRequestId?: string
  shipmentId?: string
  /** @deprecated Use shipments[].shipperAccountId instead */
  shipperAccountId?: string
  /** @deprecated Use accountId instead */
  freighterAccountId?: string
  vehicleId?: string
  status: string
  price?: number
  currency?: string
  priceBreakDown?: Record<string, unknown>
  estimatedDeliveryTime?: string
  expiresAt?: string
  createdAt: string
  updatedAt?: string
}

export const jobOfferService = {
  getAll: async () => {
    const response = await apiClient.get<ApiResponse<JobOfferDto[]>>('/api/v1/job-offers')
    return response.data.data
  },
  getById: async (jobOfferId: string) => {
    const response = await apiClient.get<ApiResponse<JobOfferDto>>(`/api/v1/job-offers/${jobOfferId}`)
    return response.data.data
  },
  getShipments: async (jobOfferId: string) => {
    const response = await apiClient.get<ApiResponse<ShipmentDto[]>>(`/api/v1/job-offers/${jobOfferId}/shipments`)
    return response.data.data
  },
  accept: async (jobOfferId: string, driverId: string) => {
    const response = await apiClient.post<ApiResponse<JobOfferDto>>(
      `/api/v1/job-offers/${jobOfferId}/accept`,
      { driverId }
    )
    return response.data.data
  },
  reject: async (jobOfferId: string) => {
    const response = await apiClient.post<ApiResponse<JobOfferDto>>(
      `/api/v1/job-offers/${jobOfferId}/reject`
    )
    return response.data.data
  },
}

// Job Services
export interface JobDto {
  id: string
  jobOfferId: string
  shipmentRequestId: string
  shipmentId: string
  shipperAccountId: string
  freighterAccountId: string
  driverId: string
  vehicleId: string
  status: string
  pickupAddress: string
  deliveryAddress: string
  scheduledPickupTime?: string
  actualPickupTime?: string
  estimatedDeliveryTime?: string
  actualDeliveryTime?: string
  price: number
  currency: string
  paymentStatus?: string
  rating?: number
  createdAt: string
  updatedAt?: string
}

export const jobService = {
  getToday: async (driverId: string) => {
    const response = await apiClient.get<ApiResponse<JobDto[]>>(
      `/api/v1/jobs/today/${driverId}`
    )
    return response.data.data
  },
  getById: async (jobId: string) => {
    const response = await apiClient.get<ApiResponse<JobDto>>(`/api/v1/jobs/${jobId}`)
    return response.data.data
  },
  start: async (jobId: string) => {
    const response = await apiClient.post<ApiResponse<JobDto>>(`/api/v1/jobs/${jobId}/start`)
    return response.data.data
  },
  updateDriver: async (jobId: string, driverId: string) => {
    const response = await apiClient.post<ApiResponse<JobDto>>(`/api/v1/jobs/${jobId}/update-driver`, {
      driverId,
    })
    return response.data.data
  },
  markPickup: async (jobId: string, shipmentId: string) => {
    const response = await apiClient.post<ApiResponse<JobDto>>(`/api/v1/jobs/${jobId}/${shipmentId}/pickup`)
    return response.data.data
  },
  markDeliver: async (jobId: string, shipmentId: string) => {
    const response = await apiClient.post<ApiResponse<JobDto>>(`/api/v1/jobs/${jobId}/${shipmentId}/deliver`)
    return response.data.data
  },
  markDelivered: async (jobId: string) => {
    const response = await apiClient.post<ApiResponse<JobDto>>(`/api/v1/jobs/${jobId}/delivered`)
    return response.data.data
  },
  delay: async (jobId: string, delayReason: string) => {
    const response = await apiClient.post<ApiResponse<JobDto>>(`/api/v1/jobs/${jobId}/delay`, {
      delayReason,
    })
    return response.data.data
  },
  cancel: async (jobId: string, cancelReason: string) => {
    const response = await apiClient.post<ApiResponse<JobDto>>(`/api/v1/jobs/${jobId}/cancel`, {
      cancelReason,
    })
    return response.data.data
  },
}

// Notification Service
export type NotificationChannel = 'PUSH' | 'SMS' | 'EMAIL' | 'WHATSAPP'
export type NotificationDeliveryStatus =
  | 'NEW'
  | 'QUEUED'
  | 'SENT'
  | 'DELIVERED'
  | 'FAILED'
  | 'ARCHIVED'
export type NotificationStatus = 'UNREAD' | 'READ'

export interface NotificationDto {
  id: string
  userId: string
  channel: NotificationChannel
  title?: string
  message?: string
  subjectType?: string
  priority?: string
  status?: NotificationStatus
  deliveryStatus?: NotificationDeliveryStatus
  sentAt?: string
  deliveredAt?: string
  readAt?: string
}

export interface BroadcastNotificationRequest {
  accountId?: string
  locale?: string
  roles?: UserRole[]
  channel: NotificationChannel
  title?: string
  message?: string
  subjectType?: string
  template: string
  priority?: string
}

export const notificationService = {
  getAll: async (params: {
    page?: number
    size?: number
    userId?: string
    channel?: NotificationChannel
    deliveryStatus?: NotificationDeliveryStatus
    from?: string
    to?: string
  } = {}) => {
    const { page = 0, size = 20, userId, channel, deliveryStatus, from, to } = params
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/notifications/admin', {
      params: { page, size, userId, channel, deliveryStatus, from, to },
    })
    return response.data.data
  },
  broadcast: async (request: BroadcastNotificationRequest) => {
    const response = await apiClient.post<ApiResponse<{ recipients: number; sent: number }>>(
      '/api/v1/notifications/admin/broadcast',
      request
    )
    return response.data.data
  },
  cleanupExpired: async () => {
    const response = await apiClient.post<ApiResponse<void>>('/api/v1/notifications/admin/cleanup')
    return response.data
  },
}

// System Health Service
export interface ServiceHealthEntry {
  status: 'UP' | 'DOWN' | 'DEGRADED' | 'UNREACHABLE' | 'UNKNOWN'
  latencyMs?: number
  url?: string
  components?: Record<string, { status?: string; details?: Record<string, unknown> }>
  error?: string
  /** Set when this entry is a sub-component pod (e.g. `shipment-service-jobs`). */
  parent?: string
  /** Role of the sub-component pod (`jobs`, `retention`, `matching`, `batch-joboffers`). */
  role?: string
}

export interface AggregatedSystemHealth {
  status: 'UP' | 'DOWN' | 'DEGRADED' | 'UNKNOWN'
  checkedAt?: string
  durationMs?: number
  totals?: { up: number; down: number; degraded: number; total: number }
  subComponentTotals?: { up: number; down: number; degraded: number; total: number }
  services?: Record<string, ServiceHealthEntry>
  error?: string
}

export const systemHealthService = {
  /** Gateway-only health (Spring actuator) — quick UP/DOWN ping. */
  getGatewayHealth: async () => {
    const response = await apiClient.get<any>('/actuator/health')
    return response.data
  },
  /** Aggregated platform health: every microservice in one shot. Admin-only. */
  getAggregatedHealth: async () => {
    const response = await apiClient.get<AggregatedSystemHealth>(
      '/api/v1/admin/system/health'
    )
    return response.data
  },
}

// ==================== Campaigns ====================
export type CampaignType =
  | 'REFERRAL'
  | 'PHOTO_SUBMISSION'
  | 'VEHICLE_STICKER'
  | 'PROMO_CODE'
  | 'GENERIC'
export type CampaignStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ENDED'
export type CampaignRewardType = 'DISCOUNT_PERCENTAGE' | 'DISCOUNT_AMOUNT' | 'FREE_PERIOD'
export type ParticipationStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REWARDED'
  | 'REJECTED'
  | 'EXPIRED'

export interface CampaignDto {
  id: string
  code: string
  name: string
  description?: string
  type: CampaignType
  status: CampaignStatus
  startDate?: string
  endDate?: string
  rewardType: CampaignRewardType
  rewardValue?: number
  rewardDurationDays?: number
  discountScope?: DiscountScope
  referredRewardValue?: number
  targetAccountType?: AccountType
  requiresApproval?: boolean
  maxRedemptions?: number
  maxRedemptionsPerAccount?: number
  redemptionCount?: number
  createdAt?: string
  updatedAt?: string
}

export interface CreateCampaignRequest {
  code: string
  name: string
  description?: string
  type: CampaignType
  startDate?: string
  endDate?: string
  rewardType: CampaignRewardType
  rewardValue?: number
  rewardDurationDays?: number
  discountScope?: DiscountScope
  referredRewardValue?: number
  targetAccountType?: AccountType
  requiresApproval?: boolean
  maxRedemptions?: number
  maxRedemptionsPerAccount?: number
  activate?: boolean
}

export type UpdateCampaignRequest = Partial<
  Omit<CreateCampaignRequest, 'code' | 'type' | 'activate'>
> & { status?: CampaignStatus }

export interface CampaignParticipationDto {
  id: string
  campaignId: string
  campaignName?: string
  campaignCode?: string
  accountId: string
  referredAccountId?: string
  referralCode?: string
  status: ParticipationStatus
  evidenceUrl?: string
  note?: string
  submittedAt?: string
  reviewedAt?: string
  reviewedBy?: string
  rejectionReason?: string
  rewardType?: CampaignRewardType
  rewardValue?: number
  rewardAppliedSubscriptionId?: string
  createdAt?: string
}

export interface ReviewParticipationRequest {
  approve: boolean
  rejectionReason?: string
}

export interface BillingCutoverResultDto {
  success?: boolean
  message?: string
  subscriptionsProcessed?: number
  subscriptionsSkipped?: number
  errors?: string[]
  completedAt?: string
}

export interface BillingSettingsDto {
  enabled: boolean
  /** A billing period without activity has the base fee it paid returned at its close. */
  inactivityFeeReturnEnabled: boolean
  persisted: boolean
  version?: number
  persistError?: string | null
  cutover?: BillingCutoverResultDto | null
}

export interface UpdateBillingSettingsRequest {
  enabled: boolean
  /** Omit to leave the current setting unchanged. */
  inactivityFeeReturnEnabled?: boolean
  description: string
}

export const billingService = {
  get: async (): Promise<BillingSettingsDto> => {
    const response = await apiClient.get<ApiResponse<BillingSettingsDto>>('/api/v1/admin/billing')
    return response.data.data
  },

  update: async (request: UpdateBillingSettingsRequest): Promise<BillingSettingsDto> => {
    const response = await apiClient.put<ApiResponse<BillingSettingsDto>>(
      '/api/v1/admin/billing',
      request,
    )
    return response.data.data
  },
}

export const campaignService = {
  list: async (page = 0, size = 20, status?: string) => {
    const response = await apiClient.get<ApiResponse<any>>('/api/v1/admin/campaigns', {
      params: { page, size, status },
    })
    return response.data.data
  },

  getById: async (campaignId: string) => {
    const response = await apiClient.get<ApiResponse<CampaignDto>>(
      `/api/v1/admin/campaigns/${campaignId}`
    )
    return response.data.data
  },

  create: async (request: CreateCampaignRequest) => {
    const response = await apiClient.post<ApiResponse<CampaignDto>>(
      '/api/v1/admin/campaigns',
      request
    )
    return response.data.data
  },

  update: async (campaignId: string, request: UpdateCampaignRequest) => {
    const response = await apiClient.put<ApiResponse<CampaignDto>>(
      `/api/v1/admin/campaigns/${campaignId}`,
      request
    )
    return response.data.data
  },

  setStatus: async (campaignId: string, status: CampaignStatus) => {
    const response = await apiClient.post<ApiResponse<CampaignDto>>(
      `/api/v1/admin/campaigns/${campaignId}/status`,
      null,
      { params: { status } }
    )
    return response.data.data
  },

  listParticipations: async (
    campaignId?: string,
    status?: string,
    page = 0,
    size = 20
  ) => {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/v1/admin/campaigns/participations',
      { params: { campaignId, status, page, size } }
    )
    return response.data.data
  },

  reviewParticipation: async (
    participationId: string,
    request: ReviewParticipationRequest
  ) => {
    const response = await apiClient.post<ApiResponse<CampaignParticipationDto>>(
      `/api/v1/admin/campaigns/participations/${participationId}/review`,
      request
    )
    return response.data.data
  },
}
